import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, TrendingUp, Calendar, MapPin, Trophy, Activity,
  User, Ruler, Brain, Zap, ClipboardList, Target,
} from 'lucide-react';
import EvaluationWizard from './EvaluationWizard';

const API = process.env.REACT_APP_API_URL ?? '';

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('prokids_token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  if (res.status === 204) return null;
  return res.json();
}

const MEASUREMENT_META = {
  SPRINT_20M:       { label: 'Sprint 20m',           unit: 's',     group: 'Físico',    lowerIsBetter: true,  icon: Zap },
  AGILITY_505:      { label: 'Agilidad 505',         unit: 's',     group: 'Físico',    lowerIsBetter: true,  icon: Zap },
  VERTICAL_JUMP:    { label: 'Salto vertical',       unit: 'cm',    group: 'Físico',    lowerIsBetter: false, icon: Zap },
  FLEXIBILITY:      { label: 'Flexibilidad',         unit: 'cm',    group: 'Físico',    lowerIsBetter: false, icon: Zap },
  BALL_CONTROL_30S: { label: 'Dominio balón 30s',    unit: 'reps',  group: 'Técnico',   lowerIsBetter: false, icon: Activity },
  PASS_ACCURACY:    { label: 'Precisión pase /10',   unit: 'hits',  group: 'Técnico',   lowerIsBetter: false, icon: Activity },
  SLALOM_DRIBBLE:   { label: 'Slalom conducción',    unit: 's',     group: 'Técnico',   lowerIsBetter: true,  icon: Activity },
  HEIGHT:           { label: 'Altura',               unit: 'cm',    group: 'Antropo.',  lowerIsBetter: false, icon: Ruler },
  WEIGHT:           { label: 'Peso',                 unit: 'kg',    group: 'Antropo.',  lowerIsBetter: false, icon: Ruler },
  CONCENTRATION:    { label: 'Concentración',        unit: '/5',    group: 'Mental',    lowerIsBetter: false, icon: Brain },
  TEAMWORK:         { label: 'Trabajo en equipo',    unit: '/5',    group: 'Mental',    lowerIsBetter: false, icon: Brain },
  LEADERSHIP:       { label: 'Liderazgo',            unit: '/5',    group: 'Mental',    lowerIsBetter: false, icon: Brain },
};

const GROUP_STYLES = {
  'Físico':   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   stroke: '#f59e0b' },
  'Técnico':  { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', stroke: '#10b981' },
  'Antropo.': { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    stroke: '#3b82f6' },
  'Mental':   { bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200',  stroke: '#a855f7' },
};

// SVG line chart for one measurement type
function Chart({ data, unit, stroke = '#10b981', lowerIsBetter = false }) {
  if (data.length < 2) {
    return (
      <div className="h-24 flex items-center justify-center text-xs text-gray-400 italic">
        {data.length === 1 ? `Última: ${data[0].value} ${unit}` : 'Sin datos aún'}
      </div>
    );
  }
  const sorted = [...data].sort((a, b) => new Date(a.measuredAt) - new Date(b.measuredAt));
  const vals = sorted.map(d => d.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const w = 300, h = 90, pad = 14;
  const points = sorted.map((m, i) => {
    const x = pad + (i / (sorted.length - 1)) * (w - pad * 2);
    const y = h - pad - ((m.value - min) / range) * (h - pad * 2);
    return [x, y, m.value];
  });
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ');

  const first = sorted[0].value;
  const last = sorted[sorted.length - 1].value;
  const improvement = lowerIsBetter ? first - last : last - first;
  const improvementPct = first !== 0 ? (improvement / first) * 100 : 0;
  const improved = improvement > 0;

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24">
        <defs>
          <linearGradient id={`grad-${stroke.replace('#','')}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.2" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${path} L ${points[points.length-1][0]} ${h-pad} L ${points[0][0]} ${h-pad} Z`} fill={`url(#grad-${stroke.replace('#','')})`} />
        <path d={path} fill="none" stroke={stroke} strokeWidth="2" />
        {points.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r="3" fill={stroke} />
        ))}
        <text x={pad} y={h-3} fontSize="9" fill="#9ca3af">{sorted[0].value.toFixed(1)}</text>
        <text x={w-pad} y={h-3} fontSize="9" fill="#9ca3af" textAnchor="end">{sorted[sorted.length-1].value.toFixed(1)}</text>
      </svg>
      <div className="flex items-center justify-between text-xs mt-1">
        <span className="text-gray-500">{sorted.length} mediciones</span>
        <span className={`font-semibold ${improved ? 'text-emerald-600' : 'text-red-600'}`}>
          {improved ? '↑' : '↓'} {Math.abs(improvementPct).toFixed(1)}%
        </span>
      </div>
    </div>
  );
}

export default function PlayerProfile({ playerId, onBack }) {
  const [player, setPlayer] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEval, setShowEval] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // No dedicated player detail endpoint — fetch via list and filter
      const [all, m] = await Promise.all([
        apiFetch('/api/v1/admin/players?limit=100'),
        apiFetch(`/api/v1/admin/players/${playerId}/measurements`),
      ]);
      const p = (all.players ?? []).find(x => x.id === playerId);
      setPlayer(p);
      setMeasurements(m.measurements ?? []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [playerId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-gray-500">Cargando perfil...</div>;
  if (!player) return (
    <div>
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <div className="bg-white rounded-2xl p-12 text-center text-gray-500">Jugador no encontrado</div>
    </div>
  );

  // Group measurements by type
  const byType = measurements.reduce((acc, m) => {
    (acc[m.type] = acc[m.type] ?? []).push(m);
    return acc;
  }, {});

  // Group types by category
  const byGroup = Object.entries(MEASUREMENT_META).reduce((acc, [type, meta]) => {
    (acc[meta.group] = acc[meta.group] ?? []).push({ type, meta, data: byType[type] ?? [] });
    return acc;
  }, {});

  const totalSessions = new Set(measurements.map(m => m.sessionId).filter(Boolean)).size;
  const typesWithData = Object.keys(byType).length;
  const lastMeasured = measurements[0]?.measuredAt;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" /> Volver a jugadores
        </button>
        <button
          onClick={() => setShowEval(true)}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-2 rounded-lg transition"
        >
          <Target className="w-4 h-4" /> Evaluación completa
        </button>
      </div>

      {showEval && (
        <EvaluationWizard
          playerId={playerId}
          playerName={player.name}
          onClose={() => { setShowEval(false); load(); }}
        />
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-start gap-5">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
            {player.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">{player.name}</h2>
            <div className="flex flex-wrap gap-3 text-sm text-gray-500 mt-1">
              <span className="flex items-center gap-1"><User className="w-4 h-4" />{player.age} años</span>
              <span className="flex items-center gap-1"><Trophy className="w-4 h-4" />{player.position}</span>
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{player.city}, {player.country}</span>
            </div>
            {player.parent && (
              <div className="text-xs text-gray-400 mt-2">
                Contacto: {player.parent.name} — {player.parent.email} — {player.parent.phone}
              </div>
            )}
          </div>
        </div>

        {/* Stats summary */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
          <div>
            <div className="text-2xl font-bold text-gray-900">{measurements.length}</div>
            <div className="text-xs text-gray-500">Mediciones totales</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">{typesWithData}/12</div>
            <div className="text-xs text-gray-500">Métricas evaluadas</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">{totalSessions}</div>
            <div className="text-xs text-gray-500">Sesiones registradas</div>
          </div>
        </div>
        {lastMeasured && (
          <div className="text-xs text-gray-400 mt-3 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Última medición: {new Date(lastMeasured).toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        )}
      </div>

      {/* Measurements by category */}
      {measurements.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <TrendingUp className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay mediciones aún. Registralas desde una sesión.</p>
        </div>
      )}

      {Object.entries(byGroup).map(([group, items]) => {
        const style = GROUP_STYLES[group];
        const hasData = items.some(i => i.data.length > 0);
        if (!hasData) return null;
        return (
          <div key={group} className={`bg-white rounded-2xl border ${style.border} overflow-hidden`}>
            <div className={`px-5 py-3 ${style.bg} ${style.text} font-bold text-sm`}>{group}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
              {items.filter(i => i.data.length > 0).map(({ type, meta, data }) => {
                const Icon = meta.icon;
                const latest = data[0];
                return (
                  <div key={type} className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${style.text}`} />
                        <div>
                          <div className="font-semibold text-sm text-gray-900">{meta.label}</div>
                          <div className="text-xs text-gray-500">Última: <span className="font-semibold text-gray-900">{latest.value} {meta.unit}</span></div>
                        </div>
                      </div>
                    </div>
                    <Chart data={data} unit={meta.unit} stroke={style.stroke} lowerIsBetter={meta.lowerIsBetter} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Raw measurement log */}
      {measurements.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-gray-500" />
            <span className="font-bold text-sm text-gray-900">Registro completo</span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2">Fecha</th>
                  <th className="text-left px-4 py-2">Métrica</th>
                  <th className="text-left px-4 py-2">Valor</th>
                  <th className="text-left px-4 py-2">Sesión</th>
                </tr>
              </thead>
              <tbody>
                {measurements.slice(0, 50).map(m => {
                  const meta = MEASUREMENT_META[m.type];
                  return (
                    <tr key={m.id} className="border-t border-gray-50">
                      <td className="px-4 py-2 text-xs text-gray-500">{new Date(m.measuredAt).toLocaleDateString('es-CL')}</td>
                      <td className="px-4 py-2 font-medium">{meta?.label ?? m.type}</td>
                      <td className="px-4 py-2 font-semibold">{m.value} {m.unit}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{m.session?.title ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
